import { apiNames, get, getNoVersion, post, patch, put } from '@general/api/shared';
import reactQueryClient from '@general/reactQueryClient';

export const fetchMember = async id => {
    const response = await get(apiNames.MEMBERSHIP, `members/${id}/profile`);
    return response?.data?.data;
};

export const fetchMembersByCompany = (companyID, companyToken) => {
    const headers = {};

    if (companyToken) {
        headers['Icsc-Company-Agent-Api-Token'] = companyToken;
    }

    return get(apiNames.MEMBERSHIP, `companies/${companyID}/members`, headers);
};

export const fetchMembersNewest = () => get(apiNames.MEMBERSHIP, `members/newest`);
export const fetchMemberDuesEligibility = id => get(apiNames.TRANSACTION, `members/${id}/dues-eligibility`);
export const fetchLoggedInUser = () => getNoVersion(apiNames.MEMBERSHIP, `oauth2/userinfo`);
export const insertMember = async (values, noLogin, companyToken) => {
    const headers = {};

    if (companyToken) {
        headers['Icsc-Company-Agent-Api-Token'] = companyToken;
    }

    const response = await post({
        apiName: apiNames.MEMBERSHIP,
        endpoint: `members${noLogin ? '?nologin' : ''}`,
        data: values,
        headers,
    });

    const newMember = response?.data?.data;

    return newMember;
};

export const updateMember = async (id, values, companyToken) => {
    const headers = {};

    if (companyToken) {
        headers['Icsc-Company-Agent-Api-Token'] = companyToken;
    }

    const response = await patch({
        apiName: apiNames.MEMBERSHIP,
        endpoint: `members/${id}`,
        data: values,
        overrideHeaders: headers,
    });

    const updatedMember = response?.data?.data;

    reactQueryClient.setQueryData(`member-${id}`, existingMemberData => {
        if (!existingMemberData) {
            return null;
        }

        const newMemberData = { ...existingMemberData };
        const fieldsUpdated = Object.keys(values);

        fieldsUpdated.forEach(field => {
            if (field === 'birthdate') {
                const { year } = values.birthdate;
                const month = values.birthdate.month.toString().padStart(2, '0');
                const day = values.birthdate.day.toString().padStart(2, '0');
                newMemberData.birthdate = `${year}-${month}-${day}`;
            } else if (field === 'business_type') {
                newMemberData.business_type = values.business_type;
            } else if (field === 'company') {
                newMemberData.company = values.company;
            } else if (field === 'company_id') {
                if (!newMemberData.company) {
                    newMemberData.company = {};
                }

                newMemberData.company.id = values.company_id;
            } else if (field === 'deferred_company_id') {
                if (!newMemberData.deferred_company) {
                    newMemberData.deferred_company = {};
                }

                newMemberData.deferred_company.id = values.deferred_company_id;
            } else if (field === 'deferred_company_name') {
                if (!newMemberData.deferred_company) {
                    newMemberData.deferred_company = {};
                }

                newMemberData.deferred_company.name = values.deferred_company_name;
            } else if (field === 'deferred_independent') {
                newMemberData.deferred_independent = values.deferred_independent;
            } else if (field === 'main_address') {
                const mainAddressIndex = newMemberData.addresses.findIndex(a => a.purpose === 'main');
                if (mainAddressIndex >= 0) {
                    newMemberData.addresses[mainAddressIndex] = updatedMember.addresses.find(a => a.purpose === 'main');
                }
            } else if (field === 'nonmember_company') {
                newMemberData.nonmember_company = values.nonmember_company;
            } else if (field === 'secondary_business_types') {
                newMemberData.secondary_business_types = values.secondary_business_types;
            } else {
                newMemberData[field] = updatedMember[field];
            }
        });

        return newMemberData;
    });

    return updatedMember;
};

export const searchMemberByEmail = ({ email }) => {
    return get(apiNames.MEMBERSHIP, `members/search/${email}`);
};

export const searchMemberByName = ({ firstName, lastName, previousCompany, previousEmail }) => {
    let queryString = `first_name=${firstName}&last_name=${lastName}`;

    if (previousCompany) {
        queryString += `&previous_company=${previousCompany}`;
    }

    if (previousEmail) {
        queryString += `&previous_email=${previousEmail}`;
    }

    return get(apiNames.MEMBERSHIP, `members/search?${queryString}`);
};

export const loginMember = (data, companyToken) => {
    const headers = {};

    if (companyToken) {
        headers['Icsc-Company-Agent-Api-Token'] = companyToken;
    }

    return post({
        apiName: apiNames.MEMBERSHIP,
        endpoint: 'login',
        data,
        headers,
    });
};

export const updateMemberAddressByPurpose = (memberID, purpose, addressData) =>
    put({
        apiName: apiNames.MEMBERSHIP,
        endpoint: `members/${memberID}/addresses/${purpose}`,
        data: addressData,
    });

export const fetchPersonalResponsibilities = () => get(apiNames.MEMBERSHIP, `personal-responsibilities`);

export const createMemberDeferredToken = async ({ memberId }) => {
    return post({
        addVersion: false,
        apiName: apiNames.MEMBERSHIP,
        endpoint: `deferred-token/${memberId}`,
    });
};

export const jsonLogin = async (username, password, resetPasswordUrl, magicLinkUrl, rememberMe = false) => {
    return post({
        apiName: apiNames.MEMBERSHIP,
        endpoint: 'login',
        data: { username, password, resetPasswordUrl, magicLinkUrl, _remember_me: rememberMe },
    });
};

export const updateProfilePassword = async (memberId, currentPassword, newPassword, confirmPassword) => {
    return put({
        apiName: apiNames.MEMBERSHIP,
        endpoint: `members/${memberId}/profile/password`,
        data: { currentPassword, newPassword: { first: newPassword, second: confirmPassword } },
    });
};

export const requestMagicLink = async (identifier, url) => {
    return post({
        apiName: apiNames.MEMBERSHIP,
        endpoint: 'magic-link',
        data: { identifier, url },
    });
};

export const authenticateMagicLink = async token => {
    return post({
        apiName: apiNames.MEMBERSHIP,
        endpoint: 'magic-link/authenticate',
        data: { token },
    });
};

export const requestPasswordReset = async (identifier, resetPasswordUrl, magicLinkUrl) => {
    return post({
        apiName: apiNames.MEMBERSHIP,
        endpoint: 'reset-password',
        data: { identifier, resetPasswordUrl, magicLinkUrl },
    });
};

export const authenticatePasswordReset = async (token, password) => {
    return post({
        apiName: apiNames.MEMBERSHIP,
        endpoint: 'reset-password/authenticate',
        data: { token, password },
    });
};

export const fetchMemberPaymentHistory = async id => {
    const response = await get(apiNames.MEMBERSHIP, `members/${id}/payment-history`);
    return response?.data?.data;
};
